import type { ScopeState } from '../scope-selection.model';
import type { ProgramGfaArea } from '@shared/models/design/activities/activity-type.model';
import type { ProjectMapPreview } from '../design/project-map-preview.model';
import type { Storey } from '../design/storey.model';
import { ModelTimestamps } from '../base.model';
import { Results } from '../unit.model';
import { ProjectFolderId } from '../folder.model';
import { Coordinate } from '../geography.model';
import { ProductCategoryId } from '../element-categories.model';
import { SemiRequired } from '../type-helpers.model';
import { BuildingDesign } from '../design/building-design.model';
import { ProductID } from '../product.model';
import { Baseline } from './baseline.model';
import { ElementKind } from './element-kind.model';
import { IElementID, ProjectID } from './ids.model';
import type { ProjectEvent } from '../project-event.model';

/**
 * @deprecated use Design model instead
 */
export interface FootprintMetadata {
  area: number;
  perimeter: number;
  coordinates: Coordinate[];
}

/**
 * @deprecated use Storey model instead
 */
export type IStorey = {
  name?: string;
  /** gross floor area */
  gfa?: number;
  /** length of the floor that meets the facade */
  facade_edge?: number;
  /** length where floor does not meet facade (atrium for example) */
  mezzanine_edge?: number;
  inner_height?: number;
};

export type IStoreyValues = SemiRequired<IStorey, 'name'>;

export type IStoreyValuesWithElevation = IStoreyValues & {
  elevation: number;
  isGroundFloor: boolean;
};

export type IIndexedStorey<StoreyType extends IStorey> = {
  floorIndex: number;
  storey: StoreyType;
};

export type IBuildingFeatures = {
  /** storeys can be mezzanine */
  mezzanine_support: boolean;
};
export const defaultBuildingFeatures: IBuildingFeatures = {
  mezzanine_support: false,
};

/**
 * Trimmed design snapshot for list/DB: only GFA when design is defined.
 * Populated from {@link BuildingDesign} (e.g. via getGfaFromStoreys) when present.
 */
export type TrimmedDesign = {
  gfa: number;
};

export type ProjectMetadata = {
  /**
   * Denormalized snapshot from {@link BuildingDesign} (GFA for project lists).
   */
  design: TrimmedDesign;

  /**
   * Manual climate target in kgCO2e per m² GFA, one per scope, keyed by
   * {@link getScopeTargetKey}. Lives on {@link Project.meta}. A scope without
   * an entry is measured against that scope's published limit, or 200 — not
   * {@link Project.target}.
   */
  scope_targets?: Record<string, number>;

  /** Presets offered in this project's selector. Omitted enables the built-in scopes. */
  enabled_scope_ids?: string[];

  /** Selected result scope, shared across project versions. */
  selected_scope?: ScopeState;

  /**
   * Compact map silhouettes for the project list. Stored on {@link Project.meta}
   * so list fetches avoid loading {@link Project.design}.
   */
  map_preview?: ProjectMapPreview;

  /**
   * Program GFA areas for list bars/donuts. Same lifecycle as {@link map_preview}.
   */
  program_areas?: ProgramGfaArea[];

  /**
   * Number of buildings in {@link Project.design}, denormalized for the list.
   */
  building_count?: number;

  /**
   * Active-version proposal rollup for list filters (may include SBEF buckets).
   */
  overview_results?: Results;

  /**
   * Active baseline rollup for list comparison (may include SBEF buckets).
   */
  baseline_results?: Results;
};
export interface ProjectUser {
  uid: string;
  write: boolean;
}

export interface Project extends ModelTimestamps {
  id: ProjectID;
  name: string;
  owner: string;
  organizations?: string[];
  template: boolean;
  archived?: boolean;
  locked?: boolean;
  description?: string;
  completed_at?: string | null;
  /**
   * @deprecated Legacy project-wide CO2e target (kgCO2e per m² GFA). Do not
   * write. Build, chart, report and the scope overlay read
   * {@link ProjectMetadata.scope_targets}. The project list still sorts and
   * draws against this leftover column.
   */
  target?: number | null;
  sharing_key?: string | null;
  /**
   * Blob object key for the report cover still when one exists
   * (`{org}/report-cover/{projectId}.{ext}`). Null/omitted when none.
   */
  cover_image_url?: string | null;
  location?: number;
  parent_id?: string | null;
  active_version_id: Baseline['id'];
  /**
   * Denormalized active-version rollup for the project list and header.
   * Numeric conversion-factor totals only — per-SBEF-group buckets live on
   * {@link Baseline.resultsRecord}, not here.
   */
  results?: Results;

  /**
   * Snapshot of market estimate data for percentile-based overrides.
   * Lazy-initialized when first override is added.
   */
  marketEstimates?: ProjectMarketEstimates;

  /**
   * Set by {@link enrichProject}. False while the client holds an
   * unenriched optimistic snapshot. The server rejects updates where
   * this is not `true`.
   */
  enriched?: boolean;

  /**
   * Human labels for effects of the change that produced this snapshot that
   * live outside the project payload and therefore survive undo (e.g. a
   * mapping saved to the resource-code table: 'Saved mapping'). The undo
   * snackbar derives a "{label} is not possible to undo." suffix from these;
   * redo needs nothing — such effects were never reverted. Travels with the
   * snapshot through enrichment and the undo/redo stacks; client-only —
   * stripped before the project is PUT to the server.
   */
  effectsOutsideUndo?: string[];

  /**
   * Element-tree baselines for the project (formerly `buildings[0].versions`).
   * A project always has at least one {@link Baseline}.
   */
  baselines: Baseline[];

  /**
   * Project geometry (formerly `buildings[0].design`). Holds location,
   * activities and the {@link Building}s with their storeys.
   */
  design: BuildingDesign;

  /**
   * Denormalized snapshot used for lists/DB (formerly the nested building wrapper's `meta`).
   */
  meta: ProjectMetadata;

  /**
   * Nodon feature flags (formerly `buildings[0].features`).
   */
  features?: IBuildingFeatures;

  /**
   * Project history shown in the comments thread, oldest first. Stored in the
   * `projects.events` column. Never copied into a duplicate or template.
   */
  events?: ProjectEvent[];
}

/**
 * Compact project-list payload. The API exposes {@link Project.meta} as
 * `metadata` and keeps heavy design/baseline data out of list responses.
 */
export interface TrimmedProject extends Omit<
  Project,
  'baselines' | 'design' | 'meta' | 'features'
> {
  /**
   * @deprecated Prefer {@link ProjectMetadata.overview_results} on {@link metadata}.
   * Kept optional for older list payloads during rollout.
   */
  overview_results?: Results;
  /**
   * @deprecated Prefer {@link ProjectMetadata.baseline_results} on {@link metadata}.
   * Kept optional for older list payloads during rollout.
   */
  baseline_results?: Results;
  /**
   * @deprecated Prefer denormalized {@link ProjectMetadata.map_preview} /
   * {@link ProjectMetadata.program_areas}. Kept for older list payloads.
   */
  program_design?:
    | BuildingDesign
    | (Omit<BuildingDesign, 'buildings'> & { storeys: Storey[] });
  /** Project meta exposed under the list API field name (map/program/results). */
  metadata: ProjectMetadata;
  version_ids: IElementID[]; // mirrors `project.baselines[i].id`
}
// TODO: Should this differ from Project?
// Shouldn't it rather just be a subset of properties inherited from Project?
export interface IProjectInfo extends Omit<
  Project,
  'baselines' | 'design' | 'meta' | 'features' | 'id'
> {
  id: string;
  parent_id: ProjectFolderId | null;
  location: number;

  kind: ElementKind.ProjectInfo;

  /**
   * List of Baselines in project
   */
  versionIds: IElementID[];
  /** First proposal in the active baseline, including SBEF buckets, for project list results. */
  overview_results?: Results;
  /** Active baseline result row for the first proposal's action difference. */
  baseline_results?: Results;
  gfa?: number;
  program_areas?: ProgramGfaArea[];
  /** Number of buildings in the design, without loading geometry in the list. */
  building_count?: number;
  map_preview?: ProjectMapPreview;
}

/**
 * @deprecated The building layer was flattened into {@link Project} (DEV-2576).
 * Kept transitionally as a structural subset of {@link Project} so helper unions
 * (`Project | IBuilding`) and {@link getBuilding} keep type-checking. Access
 * project geometry via `project.design` and baselines via `project.baselines`.
 */
export type IBuilding = Pick<
  Project,
  'meta' | 'design' | 'features' | 'baselines'
>;

/**
 * Adjustment factors at specific percentiles of market distribution.
 * Used for converting between percentile values and adjustment factors.
 */
export type PercentileFactorRange = {
  /** Which percentiles the bounds represent (e.g., { min: 5, max: 95 } for P5-P95) */
  percentileRange: { min: number; max: number };
  /** Adjustment factor at min percentile. E.g., -0.3 means -30% adjustment */
  minFactor: number;
  /** Adjustment factor at max percentile. E.g., +0.3 means +30% adjustment */
  maxFactor: number;
};

/**
 * Market estimates for a single product.
 * Contains the percentile-to-factor ranges for both carbon and cost adjustments.
 */
export type ProductMarketEstimate = {
  carbon: PercentileFactorRange;
  cost: PercentileFactorRange;
  /** Correlation factors for converting carbon adjustments to cost adjustments and vice versa
   * linear correlation is assumed(!)
   */
  coefficients: { carbonToCost: number; costToCarbon: number };
};

/**
 * Snapshot of market estimate data for a project.
 * Stored at project level to ensure calculations remain deterministic
 * even when global market estimate data is updated.
 */
export type ProjectMarketEstimates = {
  /** Default estimates used when a product lacks specific data and has no category default */
  defaults: ProductMarketEstimate;
  /** Per-category defaults used when a product lacks specific data */
  categoryDefaults: Partial<Record<ProductCategoryId, ProductMarketEstimate>>;
  /** Per-product estimates (keyed by generic product ID) */
  products: Record<ProductID, ProductMarketEstimate>;
  /** Hash of source data for detecting when global estimates have been updated */
  snapshotHash: string;
};

export type EditableProjectData = Partial<
  Pick<
    Project,
    | 'name'
    | 'description'
    | 'target'
    | 'completed_at'
    | 'template'
    | 'parent_id'
    | 'archived'
    | 'locked'
    | 'owner'
    | 'sharing_key'
  >
>;

export type CreateProjectPayloadProjectReference = {
  from: 'project_ref';
  reference: ProjectID;
  overwriteId?: ProjectID;
  data?: EditableProjectData;
};

type CreateProjectPayloadBlank = { from: 'blank'; data: EditableProjectData };
export type CreateProjectPayloadFile = { from: 'file'; data: Project };

export type CreateProjectPayload =
  | CreateProjectPayloadBlank
  | CreateProjectPayloadProjectReference
  | CreateProjectPayloadFile;

export type UpdateProjectDetailsPayload = {
  reference: ProjectID;
  overwriteId?: ProjectID;
  data: EditableProjectData;
};
